import * as Yup from 'yup';

import {
    Category,
    Ingredient,
    PackagingFormat,
    PourFormat,
    PourType,
    PriceTier,
    ServingContainer,
    Substitution,
} from '@barboxx/models/types';
import {
    CATEGORY_TYPES,
    CategoryType,
    DRINK_RECIPE_ITEM_TYPE,
    FLAVOR_TYPES,
    INGREDIENT_CATEGORIES,
    IngredientCategory,
    MEDIUM_TEXT,
    MINIMUM_TEXT,
    SHORT_TEXT,
    TINY_TEXT,
} from '@barboxx/types';
import {
    atLeastOneRequired,
    atOneOrTheOther,
    enumToList,
    transformEmptyToNull,
    volumeConsistency,
    volumeConversions,
} from '@barboxx/utils';

export const CATEGORY_SCHEMA = Yup.object<Category.Base>().shape({
    id: Yup.number().required(),
    name: Yup.string().required('Required'),
    description: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null),
    isActive: Yup.bool().default(true),
    categoryType: Yup.string().oneOf(enumToList(CATEGORY_TYPES)).required(),
    businessId: Yup.number()
        .defined()
        .nullable()
        .when('categoryType', {
            is: (type: CategoryType | undefined) =>
                type === CATEGORY_TYPES.Business,
            then: schema =>
                schema.required(
                    'Business ID is required for business categories'
                ),
            otherwise: schema => schema.default(null),
        }),
    image: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null),
});
export const CREATE_CATEGORY_SCHEMA = CATEGORY_SCHEMA.omit(['id']);

export const PRICE_TIER_SCHEMA = Yup.object<PriceTier.Base>().shape({
    id: Yup.number().required(),
    name: Yup.string().required('Required'),
    description: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null),
    displayOrder: Yup.number().transform((value?: number) =>
        !value ? 0 : value
    ),
});
export const CREATE_PRICE_TIER_SCHEMA = PRICE_TIER_SCHEMA.omit(['id']);

export const INGREDIENT_SCHEMA = Yup.object<Ingredient.Base>().shape({
    id: Yup.number().required(),
    name: Yup.string()
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(SHORT_TEXT, 'Too Long!')
        .required(),
    category: Yup.string()
        .oneOf<IngredientCategory>(enumToList(INGREDIENT_CATEGORIES))
        .defined()
        .required(),
    isAlcoholic: Yup.boolean().default(false),
    description: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(SHORT_TEXT, 'Too Long!')
        .default(null),
});

export const CREATE_INGREDIENT_SCHEMA = INGREDIENT_SCHEMA.omit(['id']);

export const SUBSTITUTION_SCHEMA = Yup.object<Substitution.Base>().shape({
    id: Yup.number().required(),
    name: Yup.string()
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(TINY_TEXT, 'Too Long!')
        .required(),
    description: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null)
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(MEDIUM_TEXT, 'Too Long!'),
    targetType: Yup.string()
        .oneOf(enumToList(DRINK_RECIPE_ITEM_TYPE))
        .defined()
        .required(),
    targetEntityId: Yup.number().required(),
    targetFlavor: Yup.string()
        .oneOf(enumToList(FLAVOR_TYPES))
        .default(null)
        .nullable()
        .defined(),
    substitutionType: Yup.string()
        .oneOf(enumToList(DRINK_RECIPE_ITEM_TYPE))
        .defined()
        .required(),
    substitutionEntityId: Yup.number().required(),
    substitutionFlavor: Yup.string()
        .oneOf(enumToList(FLAVOR_TYPES))
        .default(null)
        .nullable()
        .defined(),
});
export const CREATE_SUBSTITUTION_SCHEMA = SUBSTITUTION_SCHEMA.omit(['id']);

export const POUR_TYPE_SCHEMA = Yup.object<PourType.Base>()
    .shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
        defaultVolumeMl: Yup.number().default(null),
        defaultVolumeOz: Yup.number().default(null),
        description: Yup.string()
            .nullable()
            .transform(transformEmptyToNull)
            .default(null),
    })
    .transform(volumeConversions('defaultVolumeOz', 'defaultVolumeMl'))
    .test(
        'volume-required',
        'At least one of volumeMl or volumeOz is required',
        atLeastOneRequired('defaultVolumeOz', 'defaultVolumeMl')
    )
    .test(
        'volume-consistency',
        'volumeMl and volumeOz must be consistent',
        volumeConsistency<
            Pick<PourType.Base, 'defaultVolumeOz' | 'defaultVolumeMl'>
        >('defaultVolumeOz', 'defaultVolumeMl')
    );

export const CREATE_POUR_TYPE_SCHEMA = POUR_TYPE_SCHEMA.omit(['id']);

export const POUR_FORMAT_SCHEMA = Yup.object<PourFormat.Base>()
    .shape({
        id: Yup.number().required(),
        pourTypeId: Yup.number().required(),
        servingContainerId: Yup.number().nullable().default(null),
        packagingFormatId: Yup.number().nullable().default(null),
        productCategoryId: Yup.number().nullable().default(null),
        volumeMl: Yup.number().nullable().default(null),
        volumeOz: Yup.number().nullable().default(null),
        isDefault: Yup.boolean().default(false),
        displayOrder: Yup.number().default(0),
    })
    .transform(volumeConversions('volumeOz', 'volumeMl'))
    .test(
        'volume-required',
        'At least one of volumeMl or volumeOz is required',
        atLeastOneRequired('volumeOz', 'volumeMl')
    )
    .test(
        'volume-consistency',
        'volumeMl and volumeOz must be consistent',
        volumeConsistency<Pick<PourFormat.Base, 'volumeOz' | 'volumeMl'>>(
            'volumeOz',
            'volumeMl'
        )
    )
    .test(
        'container-or-packaging',
        'Either servingContainerId or packagingFormatId must be defined, but not both.',
        atOneOrTheOther('servingContainerId', 'packagingFormatId')
    );
export const CREATE_POUR_FORMAT_SCHEMA = POUR_FORMAT_SCHEMA.omit(['id']);

export const PACKAGING_FORMAT_SCHEMA = Yup.object<PackagingFormat.Base>().shape(
    {
        id: Yup.number().required(),
        name: Yup.string().required(),
        description: Yup.string()
            .nullable()
            .transform(transformEmptyToNull)
            .default(null),
        isSingleServe: Yup.boolean().default(true),
    }
);
export const CREATE_PACKAGING_FORMAT_SCHEMA = PACKAGING_FORMAT_SCHEMA.omit([
    'id',
]);

export const SERVING_CONTAINER_SCHEMA = Yup.object<ServingContainer.Base>()
    .shape({
        id: Yup.number().required(),
        name: Yup.string().required('Required'),
        description: Yup.string()
            .nullable()
            .transform(transformEmptyToNull)
            .default(null),
        image: Yup.string()
            .nullable()
            .transform(transformEmptyToNull)
            .default(null),
        heightMm: Yup.number().required(),
        diameterMm: Yup.number()
            .nullable()
            .transform(transformEmptyToNull)
            .default(null),
        widthMm: Yup.number()
            .nullable()
            .transform(transformEmptyToNull)
            .default(null),
        depthMm: Yup.number()
            .nullable()
            .transform(transformEmptyToNull)
            .default(null),
        volumeOz: Yup.number().required(),
    })
    .test(
        'diameter-or-width',
        'Either diameterMm or widthMm and depthMm is required',
        function (value) {
            const { diameterMm, widthMm, depthMm } = value;

            if (!diameterMm) {
                if (!widthMm && !depthMm) {
                    return this.createError({
                        path: 'diameterMm',
                        message:
                            'Either diameterMm or widthMm and depthMm is required',
                    });
                } else {
                    const missingProp = !widthMm ? 'widthMm' : 'depthMm';
                    return this.createError({
                        path: missingProp,
                        message: 'Both widthMm and depthMm is required',
                    });
                }
            } else {
                if (widthMm || depthMm) {
                    const extraProp = widthMm ? 'widthMm' : 'depthMm';

                    return this.createError({
                        path: extraProp,
                        message:
                            'diameterMm is defined, widthMm and depthMm should not be defined',
                    });
                }
            }

            return true;
        }
    );

export const CREATE_SERVING_CONTAINER_SCHEMA = SERVING_CONTAINER_SCHEMA.omit([
    'id',
]);
